<template>
  <div class="huygens-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$v₁/v₂ = sin(θ₁)/sin(θ₂)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <rect x="0" y="110" width="400" height="110" fill="rgba(6, 182, 212, 0.15)" stroke="#334155" stroke-width="2" />
          <text x="10" y="20" fill="#94a3b8" font-size="10">介质 1 (波速 v₁ 较快)</text>
          <text x="10" y="210" fill="#06b6d4" font-size="10">介质 2 (波速 v₂ 较慢)</text>

          <g stroke="#cbd5e1" stroke-width="2" opacity="0.8">
             <line v-for="(wf, i) in incidentWavefronts" :key="'in'+i" 
                   :x1="wf.x1" :y1="wf.y1" :x2="wf.x2" :y2="wf.y2" />
          </g>

          <g>
             <path v-for="(wav, i) in secondaryWavelets" :key="'wav'+i" 
                   :d="`M ${wav.cx - wav.r}, ${wav.cy} A ${wav.r} ${wav.r} 0 0 0 ${wav.cx + wav.r}, ${wav.cy}`" 
                   fill="rgba(6, 182, 212, 0.1)" stroke="#0ea5e9" stroke-width="1.5" />
             <circle v-for="(wav, i) in secondaryWavelets" :key="'src'+i" 
                   :cx="wav.cx" :cy="wav.cy" r="2" fill="#ef4444" />
          </g>

          <g stroke="#22c55e" stroke-width="3" opacity="0.9">
             <line v-if="refractedWavefront" 
                   :x1="refractedWavefront.x1" :y1="refractedWavefront.y1" 
                   :x2="refractedWavefront.x2" :y2="refractedWavefront.y2" />
          </g>
          
          <text v-if="refractedWavefront" :x="refractedWavefront.x1 + 10" :y="refractedWavefront.y1 + 15" fill="#22c55e" font-size="10" font-weight="bold">包络面 (折射波阵面)</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>介质 1 波速 v₁</label>
              <span class="val">{{ v1.toFixed(1) }} <small>单位</small></span>
           </div>
           <div class="hud-item orange">
              <label>介质 2 波速 v₂</label>
              <span class="val">{{ v2.toFixed(1) }} <small>单位</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节介质2波速折减 (模拟折射率倒数)</label>
                    <el-slider v-model="v2Modifier" :min="0.3" :max="0.9" :step="0.1" size="small"></el-slider>
                 </div>
                 <div class="flex-1">
                    <el-button type="primary" size="small" plain @click="replay" class="w-full" icon="el-icon-refresh">重新发射平波</el-button>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>惠更斯原理认为：波阵面上每个点都可以看作发出子波的新<strong>波源</strong>，其后形成的包络面就是真实的新波阵面。如动画模拟：平面波倾斜入射边界，左端 A 点先接触介质 2，在相对迟缓的 <code>v₂</code> 波速中散发球面子波；右端 B 点还在以 <code>v₁</code> 飞行。当 B 终于触底时，A 的子波半径恰为 <code>r = v₂·Δt</code>。作所有边界子波公切线所构成的<strong>包络面</strong>，自然顺理成章完成了偏折，此直接物理构图推导出了斯涅尔定律！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HuygensPrincipleViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      time: 0,
      timer: null,
      v1: 50,
      v2Modifier: 0.5,
      angle1Deg: 30, 
      emissionInterval: 2, 
      wavefronts: [] 
    }
  },
  computed: {
    v2() { return this.v1 * this.v2Modifier; },
    incidentWavefronts() {
       let lines = [];
       const L = 200;
       for(let tOffset = 0; tOffset < this.time; tOffset += 1.5) {
          let t = this.time - tOffset; 
          if (t < 0) continue;
          
          let cx0 = 100, cy0 = -50;
          let theta1 = this.angle1Deg * Math.PI / 180;
          let dirX = Math.sin(theta1);
          let dirY = Math.cos(theta1);
          
          let currentCx = cx0 + dirX * this.v1 * t;
          let currentCy = cy0 + dirY * this.v1 * t;
          
          let hDirX = -Math.cos(theta1);
          let hDirY = Math.sin(theta1);
          
          let xA = currentCx + hDirX * (L/2);
          let yA = currentCy + hDirY * (L/2);
          let xB = currentCx - hDirX * (L/2);
          let yB = currentCy - hDirY * (L/2);
          
          if (yA < 110 || yB < 110) {
             let clippx1 = xA, clippy1 = yA;
             let clippx2 = xB, clippy2 = yB;
             if(clippy1 > 110) {
                let frac = (110 - yA) / (yB - yA);
                clippx1 = xA + frac * (xB - xA);
                clippy1 = 110;
             }
             if(clippy2 > 110) {
                let frac = (110 - yA) / (yB - yA);
                clippx2 = xA + frac * (xB - xA);
                clippy2 = 110;
             }
             if (clippy1 !== 110 || clippy2 !== 110 || Math.abs(clippx1-clippx2) > 1) {
                lines.push({ x1: clippx1, y1: clippy1, x2: clippx2, y2: clippy2 });
             }
          }
       }
       return lines;
    },
    secondaryWavelets() {
       let wavelets = [];
       const L = 200;
       let cx0 = 100, cy0 = -50;
       let theta1 = this.angle1Deg * Math.PI / 180;
       let dirX = Math.sin(theta1);
       let dirY = Math.cos(theta1);
       let hDirX = -Math.cos(theta1);
       let hDirY = Math.sin(theta1);
       
       let xA0 = cx0 + hDirX * (L/2);
       let yA0 = cy0 + hDirY * (L/2);
       let xB0 = cx0 - hDirX * (L/2);
       let yB0 = cy0 - hDirY * (L/2);
       
       const N = 7;
       for(let i=0; i<N; i++) {
          let px0 = xA0 + (xB0 - xA0) * (i / (N-1));
          let py0 = yA0 + (yB0 - yA0) * (i / (N-1));
          
          let distToBoundary = 110 - py0;
          let timeToHit = distToBoundary / (dirY * this.v1);
          
          if (timeToHit > 0 && this.time > timeToHit) {
             let hitX = px0 + dirX * this.v1 * timeToHit;
             let timeInMed2 = this.time - timeToHit;
             let r = this.v2 * timeInMed2;
             wavelets.push({ cx: hitX, cy: 110, r: r });
          }
       }
       return wavelets;
    },
    refractedWavefront() {
       let wls = this.secondaryWavelets;
       if (wls.length < 2) return null;
       
       let firstHit = wls[0]; 
       let lastHit = wls[wls.length-1]; 
       
       let theta1 = this.angle1Deg * Math.PI / 180;
       let sin2 = (this.v2 / this.v1) * Math.sin(theta1);
       let theta2 = Math.asin(sin2);
       
       let nx = Math.sin(theta2);
       let ny = Math.cos(theta2);
       
       let envX1 = firstHit.cx + nx * firstHit.r;
       let envY1 = firstHit.cy + ny * firstHit.r;
       let envX2 = lastHit.cx + nx * lastHit.r;
       let envY2 = lastHit.cy + ny * lastHit.r;
       
       if (envY1 < 111) return null; 
       return { x1: envX1, y1: envY1, x2: envX2, y2: envY2 };
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/折射/.test(kp)) this.v2Modifier = 0.5;
      else this.v2Modifier = 0.8;
    },
    animate() {
       this.time += 0.02;
       if (this.time > 8) this.time = 0;
       this.timer = requestAnimationFrame(this.animate);
    },
    replay() { this.time = 0; }
  },
  mounted() { this.animate(); },
  beforeDestroy() { if(this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.huygens-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; } .w-full { width: 100%; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
